import React, { forwardRef, memo, useCallback, useContext, useRef } from 'react'
import { ScreenContext } from 'react-components'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import HeroSearchbar from './hero-searchbar'
import { BackgroundImages, Container, ContentWrapper, SubtitleWrapper, TitleWrapper } from './styles'
import { useScrollToSearch } from '../../../hooks/scroll-to-search'
import SliderSection from './slider-section'
import { HOME_PAGE_TYPE } from '../../../constants/home'
import { LOCALES } from '../../../common/constants'
import { useLocale } from '../../../hooks/locale'

type Navigation = HeaderSectionTransport['navigationSectionResponse']['navigation']

interface CommonHeroSectionProps {
    title: string
    subtitle: string
    backgroundImages: BackgroundImages
    logo?: Navigation['logo']
    textColor?: string
    hasOverlay?: boolean
    initialLocation?: string
    activeHomeType?: HOME_PAGE_TYPE
    isBorderGradientEnabled?: boolean
    showSliderSection?: boolean
}

const CommonHeroSection = forwardRef<HTMLDivElement, CommonHeroSectionProps>(
    (
        {
            title,
            subtitle,
            backgroundImages,
            logo,
            textColor,
            hasOverlay,
            initialLocation,
            activeHomeType,
            isBorderGradientEnabled,
            showSliderSection,
        },
        ref,
    ) => {
        const { isDesktop, isTablet } = useContext(ScreenContext)
        const innerRef = useRef<HTMLDivElement | null>(null)
        const { locale } = useLocale()

        const isSticky = useScrollToSearch(innerRef, isDesktop || isTablet)

        const setRefs = useCallback(
            (element: HTMLDivElement | null) => {
                innerRef.current = element
                if (typeof ref === 'function') {
                    ref(element)
                } else if (ref) {
                    ref.current = element
                }
            },
            [ref],
        )

        return (
            <Container backgroundImages={backgroundImages} hasOverlay={hasOverlay}>
                <ContentWrapper ref={setRefs}>
                    <TitleWrapper textColor={textColor}>{title}</TitleWrapper>
                    <SubtitleWrapper textColor={textColor}>{subtitle}</SubtitleWrapper>
                    {showSliderSection && <SliderSection activeHomeType={activeHomeType} />}
                    <HeroSearchbar
                        initialLocation={initialLocation}
                        isSticky={isSticky}
                        logo={logo}
                        petsAllowed={false}
                        activeHomeType={activeHomeType}
                        isBorderGradientEnabled={isBorderGradientEnabled}
                    />
                </ContentWrapper>
            </Container>
        )
    },
)

CommonHeroSection.displayName = 'CommonHeroSection'

export default memo(CommonHeroSection)
